import React from 'react';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import nextheralogo from '../assets/nextheralogo.png';

const CookiePolicy = () => {
  const handleGoBack = () => {
    window.location.href = '/';
  };

  return (
    <PolicyContainer>
      <PolicyHeader>
        <BackButton onClick={handleGoBack}>
          <ArrowLeft size={20} />
          Back to Home
        </BackButton>
        <Logo src={nextheralogo} alt="NexThera" />
      </PolicyHeader>

      <PolicyContent>
        <Title>Cookie Policy</Title>
        <LastUpdated>Last updated: 24 August 2025</LastUpdated>
        
        <Section>
          <Paragraph>
            This Cookie Policy explains how NEXTHERAPEUTICS, LDA ("NexThera", "we", "us") uses cookies and similar technologies on our website. It should be read together with our Privacy Policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Who we are</SectionTitle>
          <Paragraph>
            NEXTHERAPEUTICS, LDA (NIPC/VAT PT518543404) operates this site from our registered head office at Rua Junta de Freguesia, 194, CI3 Nextherapeutics LDA, Vila Pavão, Escariz, 4540-322 Arouca, Portugal. You can reach us at <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink>.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>What are cookies?</SectionTitle>
          <Paragraph>
            Cookies are small text files placed on your device by a website. They help the site work, keep it secure, remember your preferences, and - in some cases - measure how the site is used. We also use closely related technologies such as local storage, tags, pixels and SDKs. In this policy, we call them all "cookies".
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>How we use cookies</SectionTitle>
          <Paragraph>
            Our starting point is simple: we only use what we need, and we ask for your permission before setting anything that isn't essential. Essential cookies keep the site stable and secure (things like load-balancing, session management and storing your cookie choices). If you consent, we add privacy-respecting analytics to understand what's working and where we can improve, and a few functional helpers (for example, remembering language). If we run marketing campaigns, we may ask your permission to use additional cookies to measure those campaigns. If you change your mind, you can update your choices at any time.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Your choices</SectionTitle>
          <Paragraph>You control non-essential cookies.</Paragraph>
          <List>
            <ListItem>Use our Cookie Settings panel (available in the footer) to accept or refuse optional categories.</ListItem>
            <ListItem>You can also use your browser settings to block or delete cookies.</ListItem>
            <ListItem>If you refuse or delete cookies, the site will still work, but some features may be limited.</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Legal basis</SectionTitle>
          <Paragraph>
            Under the EU ePrivacy rules and the GDPR, we rely on legitimate interests to use essential cookies that keep the website secure and functional. For all non-essential cookies (analytics, functional preferences beyond strictly necessary, and any marketing measurement), we rely on your consent. You can withdraw consent at any time via Cookie Settings - this won't affect the lawfulness of processing before withdrawal.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>The cookie categories we use</SectionTitle>
          <Paragraph>We keep this section straightforward so you can see, at a glance, what's happening on your device.</Paragraph>
          
          <CookieCategory>
            <CategoryTitle>Essential</CategoryTitle>
            <CategoryDescription>
              Keep the site running and safe: load balancing, basic session continuity, security protections, and storing your cookie preferences. These do not track you across sites, and they expire quickly or when your session ends.
            </CategoryDescription>
          </CookieCategory>

          <CookieCategory>
            <CategoryTitle>Analytics</CategoryTitle>
            <CategoryDescription>
              Help us understand traffic, pages viewed, and general usage patterns - so we can improve the experience. Where feasible we apply IP truncation/pseudonymisation, and providers process data on our instructions.
            </CategoryDescription>
          </CookieCategory>

          <CookieCategory>
            <CategoryTitle>Functional</CategoryTitle>
            <CategoryDescription>
              Remember optional preferences (for example, language, UI settings) to make the site more convenient on your next visit.
            </CategoryDescription>
          </CookieCategory>

          <CookieCategory>
            <CategoryTitle>Marketing/measurement</CategoryTitle>
            <CategoryDescription>
              Used to measure the effectiveness of campaigns or events. We don't allow third parties to use these for their own advertising on our site unless you enable them explicitly.
            </CategoryDescription>
          </CookieCategory>
        </Section>

        <Section>
          <SectionTitle>How long do cookies last</SectionTitle>
          <Paragraph>
            Some cookies last only for the session and are deleted when you close your browser. Others are persistent and remain for a defined period (typically 3 - 13 months, shorter where feasible), so your preferences or analytics sampling settings are remembered.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Third parties and international transfers</SectionTitle>
          <Paragraph>
            We prefer to use EU-hosted services. Where we work with specialist providers (for hosting, analytics, security, email delivery or event sign-ups), they act under contract as our processors and must follow our instructions. If data is transferred outside the EEA/UK/Switzerland, we use recognised safeguards such as EU adequacy decisions or the European Commission's Standard Contractual Clauses with supplementary measures as needed. You can ask us for more information via the contact details below.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Keeping this page current</SectionTitle>
          <Paragraph>
            The cookies we use can change - for example, when we add a feature or swap a vendor. The Cookie Settings panel always reflects the current list, purpose and lifetime for each cookie, and this Policy will be updated when changes are material.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Contact us</SectionTitle>
          <Paragraph>
            Questions about cookies or your choices? Email <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink> (subject: "Cookies"). Postal enquiries can be sent to our registered head office in Arouca, Portugal.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Regional notes</SectionTitle>
          
          <RegionalNote>
            <RegionalTitle>United Kingdom</RegionalTitle>
            <Paragraph>
              For UK visitors, references to the GDPR mean the UK GDPR and the Data Protection Act 2018. International transfers from the UK use the UK Addendum to the EU Standard Contractual Clauses or the UK IDTA, as applicable. You can raise concerns with the UK ICO.
            </Paragraph>
          </RegionalNote>

          <RegionalNote>
            <RegionalTitle>California (USA)</RegionalTitle>
            <Paragraph>
              Some cookies used for targeted advertising can be considered a "sale" or "sharing" of personal information under the CPRA. We do not set such cookies unless you choose to enable them. If enabled, you may opt out at any time via Cookie Settings ("Do Not Sell or Share My Personal Information"). We do not discriminate against you for exercising your rights.
            </Paragraph>
          </RegionalNote>
        </Section>
      </PolicyContent>
    </PolicyContainer>
  );
};

const PolicyContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  padding: 0;
`;

const PolicyHeader = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #2094F1;
  }
`;

const Logo = styled.img`
  height: 50px;
  width: auto;
  margin: 0;
`;

const PolicyContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  text-align: center;
  margin-bottom: 3rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const EmailLink = styled.a`
  color: #2094F1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CookieCategory = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const CategoryTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2094F1;
  margin-bottom: 0.5rem;
`;

const CategoryDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
`;

const RegionalNote = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const RegionalTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 0.5rem;
`;

export default CookiePolicy;
